import {
 createContext, useContext, useState, useEffect, useCallback, ReactNode
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
 Database
} from 'lucide-react';

// Define the structure for the table
interface Table {
    id: string;
    name: string;
    icon: React.ComponentType; // The icon is a React component (like `Database` from `lucide-react`)
}

// Define the context state type
interface RoutingContextType {
    currentTable: string;
    currentView: string;
    navigate: (table: string, view?: string) => void;
    navigateToSpecialRoute: (route: string) => void;
    specialRoute: string | null;
    setSpecialRoute: (route: string | null) => void;
    fetchTables: () => void;
    addTable: (table: Table) => void;
    tables: Table[];
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    getTableName: (tableId: string) => string;
    updateTableName: (tableId: string, newName: string) => void;
    reloadKey: number;
    triggerReload: () => void;
}

// Create a context for routing with the appropriate typing
const RoutingContext = createContext<RoutingContextType | null>(null);

// Create a custom hook to access the routing context
export function useRouting(): RoutingContextType {
    const context = useContext(RoutingContext);
    if (!context) {
        throw new Error('useRouting must be used within a RoutingProvider');
    }
    return context;
}

// Define the props for RoutingProvider
interface RoutingProviderProps {
    children: ReactNode;
}

export function RoutingProvider({
 children
}: RoutingProviderProps) {
    const [currentTable, setCurrentTable] = useState<string>('dashboard');
    const [currentView, setCurrentView] = useState<string>('records');
    const [basePath, setBasePath] = useState<string>('');
    const [specialRoute, setSpecialRoute] = useState<string | null>(null);
    const [tables, setTables] = useState<Table[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const {
 oragID, appId
} = useParams<{ oragID: string; appId: string }>(); // Typing for `oragID` and `appId`

    const triggerReload = () => {
        setReloadKey(prev => prev + 1);
    };

    const navigate = (table: string, view = 'records') => {
        setCurrentTable(table);
        setSpecialRoute(null); // Reset specialRoute when navigating to tables

        if (table === 'dashboard') {
            setCurrentView('records');
            window.history.pushState({}, '', `${basePath}/tables`);
        } else {
            setCurrentView(view);
            window.history.pushState({}, '', `${basePath}/tables/${table}/${view}`);
        }
    };

    const navigateToSpecialRoute = (route: string) => {
        setSpecialRoute(route);
        // Update the URL
        window.history.pushState({}, '', `${basePath}/tables/${route}`);
    };

    const fetchTables = async () => {
        try {
            setIsLoading(true);

            const response = await schemaService.retrieveSchemas(null, appId, oragID, null);

            const fetchedTables = response?.data?.schemas || [];

            const formattedTables = fetchedTables.map((table, index) => {
                const tableName = table.tableName?.trim() || `Untitled-${index}`; // Fallback name if empty
                return {
                    id: tableName || `table-${index}`,
                    name: tableName,
                    icon: Database,
                };
            });

            setTables(formattedTables);
        } catch (e) {
            console.error('Error retrieving schemas: ', e);
        } finally {
            setIsLoading(false);
        }
    };

    const addTable = (table: Table) => {
        setTables(prevTables => {
            const updatedTables = [...prevTables, table];
            localStorage.setItem('tables', JSON.stringify(updatedTables));
            return updatedTables;
        });
        fetchTables();
    };

    const getTableName = useCallback(
        (tableId: string): string => {
            const table = tables.find(t => t.id === tableId);
            return table ? table.name : tableId;
        },
        [tables]
    );

    const updateTableName = useCallback((tableId: string, newName: string) => {
        setTables(prevTables =>
            prevTables.map(table =>
                table.id === tableId ? {
 ...table,
name: newName
} : table
            )
        );
    }, []);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const path = window.location.pathname;
            const pathParts = path.split('/').filter(Boolean);

            const tableIndex = pathParts.indexOf('tables');
            const dynamicBasePath = tableIndex !== -1 ? `/${pathParts.slice(0, tableIndex).join('/')}` : `/${pathParts.join('/')}`;
            setBasePath(dynamicBasePath);

            if (pathParts.length === 1 && ['create-table-ai', 'blueprints'].includes(pathParts[0])) {
                setSpecialRoute(pathParts[0]);
                setCurrentTable(null);
                setCurrentView(null);
                return;
            }

            setSpecialRoute(null);

            if (tableIndex !== -1) {
                if (pathParts.length > tableIndex + 1) {
                    setCurrentTable(pathParts[tableIndex + 1]);
                    setCurrentView(pathParts[tableIndex + 2] || 'records');
                } else {
                    setCurrentTable('dashboard');
                    setCurrentView('records');
                }
            } else {
                setCurrentTable('dashboard');
                setCurrentView('records');
            }
        };

        window.addEventListener('popstate', handlePopState);
        handlePopState();

        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    return (
        <RoutingContext.Provider value={{
            currentTable,
            currentView,
            navigate,
            navigateToSpecialRoute,
            specialRoute,
            setSpecialRoute,
            fetchTables,
            addTable,
            tables,
            isLoading,
            setIsLoading,
            getTableName,
            updateTableName,
            reloadKey,
            triggerReload,
        }}>
            {children}
        </RoutingContext.Provider>
    );
}
